import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Container } from '@mui/material';
import { ErrorBoundaryHelper } from '../utils/errorHandler';
import { errorMonitoring } from '../utils/errorMonitoring';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    this.setState({
      error,
      errorInfo,
    });

    // INFRA-05:單一輸出路徑 — 舊版 ErrorBoundaryHelper.logError(console.error)
    // 與 errorMonitoring.reportError(logger.error)對同一錯誤重複記錄兩次
    errorMonitoring.reportError(error, {
      component: errorInfo.componentStack?.split('\n')[1]?.trim(),
      errorBoundary: true,
      severity: 'high',
      category: 'ui',
      metadata: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRefresh = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const errorMessage = this.state.error 
        ? ErrorBoundaryHelper.getErrorMessage(this.state.error)
        : '應用程式發生錯誤';

      return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="60vh"
            textAlign="center"
            gap={3}
          >
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                糟糕！出了點問題
              </Typography>
              <Typography variant="body2">
                {errorMessage}
              </Typography>
            </Alert>

            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
                size="large"
              >
                重新載入
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                size="large"
              >
                回到首頁
              </Button>
            </Box>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  width: '100%',
                  maxWidth: 600,
                }}
              >
                <Typography variant="subtitle2" color="error" gutterBottom>
                  開發模式錯誤詳情：
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.stack}
                </Typography>
                
                {this.state.errorInfo && (
                  <>
                    <Typography variant="subtitle2" color="error" sx={{ mt: 2 }} gutterBottom>
                      組件堆疊：
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
